import { type FieldValues, useController } from 'react-hook-form';
import type { TypedUseControllerProps } from '../FormControl/FormControl.types';
import { FormField } from '../FormField/FormField';
import type { FormFieldBaseProps } from '../FormField/FormField.types';
import { SegmentedButton } from '../SegmentedButton/SegmentedButton';
import type { SegmentedButtonBaseProps } from '../SegmentedButton/SegmentedButton.types';

type FormSegmentedButtonMultiProps<
  TFieldValues extends FieldValues = FieldValues,
  TValue extends string = string,
> = TypedUseControllerProps<TFieldValues, TValue[]> &
  FormFieldBaseProps &
  SegmentedButtonBaseProps;

type FormSegmentedButtonSingleProps<
  TFieldValues extends FieldValues = FieldValues,
  TValue extends string | null = string | null,
> = TypedUseControllerProps<TFieldValues, TValue> &
  FormFieldBaseProps &
  SegmentedButtonBaseProps;

/*
 *  FormSegmentedButton encapsulates the SegmentedButton component and provides a form control with validation to be used with React Hook Form.
 */
const Multi = <
  TFieldValues extends FieldValues = FieldValues,
  TValue extends string = string,
>({
  disabled,
  children,
  variant,
  ...props
}: FormSegmentedButtonMultiProps<TFieldValues, TValue>) => {
  const {
    field: { ref, ...field },
    formState: { errors },
  } = useController(props);

  const error = errors[props.name]?.message?.toString();

  return (
    <FormField
      {...props}
      disabled={disabled}
      error={error}
      htmlFor={props.name}
    >
      <SegmentedButton.Multi {...field} variant={variant} innerRef={ref}>
        {children}
      </SegmentedButton.Multi>
    </FormField>
  );
};

const Single = <
  TFieldValues extends FieldValues = FieldValues,
  TValue extends string | null = string | null,
>({
  disabled,
  children,
  variant,
  ...props
}: FormSegmentedButtonSingleProps<TFieldValues, TValue>) => {
  const {
    field: { ref, ...field },
    formState: { errors },
  } = useController(props);

  const error = errors[props.name]?.message?.toString();

  return (
    <FormField
      {...props}
      disabled={disabled}
      error={error}
      htmlFor={props.name}
    >
      <SegmentedButton.Single {...field} variant={variant} innerRef={ref}>
        {children}
      </SegmentedButton.Single>
    </FormField>
  );
};

export const FormSegmentedButton = { Multi, Single };
